import api from '@/api'
import Vue from 'vue';
import permissions from '@/api/permissions'
import _ from 'lodash'

const fillAccreditation = function(accreditation) {
  return {
    id: accreditation.id,
    owner: accreditation.owner,
    type: `accreditation-type.${(accreditation.type || '').toLowerCase()}`,
    state: new api.AccreditationState(accreditation.state),
    start: accreditation.start,
    expired: accreditation.expired,
    reason: accreditation.reason,
    permissions: permissions.init(accreditation.permissions || []),
    hasSign: !!accreditation.hasSign,
    documents: _.sortBy(accreditation.documents.map((doc) => ({
      ...doc,
      state: new api.AccreditationDocumentState(doc.state),
      expirationDate: doc.time ? new Date(doc.time) : undefined,
      days: _.isFinite(doc.days) ? doc.days : undefined
    })), 'num')
  };
};

export default {
  state: {
    current: {
      user: undefined,
      organization: undefined,
      accreditation: undefined,
      notifications: []
    },

    users: {},

    accreditations: {},
  },

  getters: {
    currentUser: s => s.current.user,
    currentAccreditation: s => s.current.user.accreditationId ? s.accreditations[s.current.user.accreditationId] : undefined,
    currentOrganization: s => s.current.organization,
    notifications: s => s.current.notifications,
    user: s => userId => s.users[userId],
    accreditation: s => accId => s.accreditations[accId]
  },

  mutations: {
    initCurrentUser: function (s, profile) {
      s.current.user = s.users[profile.id];

      if (profile.accreditation) {
        s.current.accreditation = s.accreditations[profile.accreditation.id]
      }

      if (profile.organization) {
        s.current.organization = this.getters.organization(profile.organization.id);
      }
    },

    clearAccount: s => s.current.user = undefined,

    setAccount: (s, user) => {
      const model = {
        id: user.id,
        roles: [new api.UserRole(user.role)],
        email: {
          verified: user.email,
          unverified: user.emailNew
        },
        phone: {
          verified: user.phone,
          unverified: user.phoneNew
        },
        state: new api.UserState(user.state),
        role: new api.UserRole(user.role),
        online: user.isOnline,
        counters: {
          lots: +user.countCommitLot || 0,
          tenders: +user.countCommitTender || 0,
          signatures: +user.countProcessingEp || 0
        },
        messages: {
          notRead: user.newMessages,
          received: user.inMessages,
          sent: user.outMessages,
          deleted: user.delMessages,
        },
        accreditations: _.orderBy((user.accreditations || []).map(acc => fillAccreditation(acc)), 'expired'),
        firstName: user.firstName,
        lastName: user.lastName,
        middleName: user.middleName,
        permissions: permissions.init(user.permissions),
        isMain: !!user.mainUser,
        inCommission: !!user.comissionMember,
        needManualReview: !!user.manualReview,
        parent: user.parent,
        passport: {
          series: user.passportSerial,
          id: user.passportNum
        },
        signatures: user.ep.map(e => ({
          id: e.id,
          creationDate: e.time ? new Date(e.time) : undefined,
          expirationDate: e.expired ? new Date(e.expired) : undefined,
          state: new api.SignatureState(e.state),
          reason: e.reason,
          permissions: permissions.init(e.permissions),
          hasSign: !!e.hasSign
        })),
        mainUser: user.parent || undefined,
        favorites: user.favorites || [],
        categories: user.categories,
        department: user.department,
        lastChangeTimestamp: user.lastChange,
        lastLoginTimestamp: user.lastLogin,
        lastLoginIp: user.lastLoginIp,
        registrationTimestamp: user.dtReg,
        organizationId: user.organizationId || (user.organization ? user.organization.id : undefined),
        accreditationId: user.accreditationId || (user.accreditation ? user.accreditation.id : undefined)
      };

      if (model.inCommission) {
        model.roles = model.roles.filter(e => e.valid);
        model.roles.push(new api.UserRole(api.UserRole.COMMISSION_MEMBER));
      }

      Vue.set(s.users, model.id, model);
    },

    setNotifications: (s, data) => {
      s.current.notifications = _.orderBy((data || []).map(e => {
        const r = {
          id: e.id,
          objectId: e.objectId,
          wasRead: !!e.hasSeen,
          isActionRequired: !!e.needAction,
          receivedTime: new Date(e.time),
          type: new api.NotificationType(e.typeNotification)
        };

        try {
          r.data = JSON.parse(e.json);
        }
        catch (e) {
          r.data = e.json;
        }

        return r;
      }).filter(e => !e.wasRead), 'receivedTime', 'desc');
    },

    setAccreditation: (s, accreditation) => {
      const model = fillAccreditation(accreditation);
      Vue.set(s.accreditations, model.id, model);
    }
  },

  actions: {
    async fetchUser({commit, dispatch, getters}, info) {
      try {
        const response = await api.get(`/account/${info.userId}`);

        const {status, result, errorCode} = response.data;

        if (status === 200) {
          return await dispatch('handleProfile', {
            profile: result,
            currentUser: result.id === getters.currentUser.id
          });
        }
        else {
          throw new api.Errors(errorCode);
        }
      }
      catch(e) {
        console.error('Something went wrong while fetching user profile', e);
        throw e;
      }
    },

    handleProfile({commit, dispatch, getters}, payload) {
      const profile = payload.profile;

      commit('setAccount', profile);

      if (!_.isEmpty(profile.organization)) {
        commit('setOrganization', profile.organization);
      }

      if (!_.isEmpty(profile.accreditation)) {
        commit('setAccreditation', profile.accreditation);
      }

      if (payload.currentUser) {
        commit('initCurrentUser', profile);

        if (!_.isEmpty(profile.notifications)) {
          commit('setNotifications', profile.notifications);
        }
      }

      return getters.user(profile.id);
    },

    async resendEmailConfirmation() {
      let error;

      try {
        const response = await api.post('/account/requestEmail');
        const {status, errorCode} = response.data;

        if (status !== 200) {
          error = new api.Errors(errorCode);
        }
      }
      catch (e) {
        error = e;
      }

      if (error) {
        throw error;
      }
    },

    async resendPhoneConfirmation({}) {
      let error;

      try {
        const response = await api.post('/account/requestSms');
        const {status, errorCode, result} = response.data;

        if (status === 200) {
          return {
            userId: result.id,
            eventId: result.eventId
          }
        }
        else {
          error = new api.Errors(errorCode);
        }
      }
      catch (e) {
        error = e;
      }

      if (error) {
        throw error;
      }
    },

    async rollbackEmail({dispatch}) {
      try {
        const response = await api.post('/account/resetEmail');
        const {status, result, errorCode} = response.data;

        if (status === 200) {
          await dispatch('handleProfile', {
            profile: result,
            currentUser: true
          });
        }
        else {
          throw new api.Errors(errorCode);
        }
      }
      catch (e) {
        throw e;
      }
    },

    async rollbackPhone({dispatch}) {
      try {
        const response = await api.post('/account/resetPhone');
        const {status, result, errorCode} = response.data;

        if (status === 200) {
          await dispatch('handleProfile', {
            profile: result,
            currentUser: true
          });
        }
        else {
          throw new api.Errors(errorCode);
        }
      }
      catch (e) {
        throw e;
      }
    },

    async recoveryPassword({}, { email }) {
      try {
        const response = await api.post('/account/recovery', { email });
        const {status, errorCode} = response.data;

        if (status !== 200) {
          throw new api.Errors(errorCode);
        }
      }
      catch (e) {
        throw e;
      }
    },

    async modifyAccount({dispatch}, {id, lastName, firstName, middleName, email, phone, passportId, passportSeries, categories, department}) {
      try {
        const response = await api.put(`account/${id}`, {
          firstName,
          lastName,
          middleName,
          email,
          phone,
          passportNum: passportId,
          passportSerial: passportSeries,
          categories: categories,
          department: department
        });
        const {status, result, errorCode} = response.data;

        if (status === 200) {
          await dispatch('handleProfile', {
            profile: result,
            currentUser: true
          });
        }
        else {
          throw new api.Errors(errorCode);
        }
      }
      catch (e) {
        console.error('Something went wrong while logging user in', e);
        throw e;
      }
    },

    async changePassword({}, {id, previousPassword, newPassword}) {
      let error;

      try {
        const response = await api.post(`account/setNewPassword`, {
          oldPassword: previousPassword,
          newPassword: newPassword
        });
        const {status, errorCode} = response.data;

        if (status !== 200) {
          error =  new api.Errors(errorCode);
        }
      }
      catch (e) {
        console.error('Something went wrong while logging user in', e);
        error = e;
      }

      if (error) {
        throw error;
      }
    },

    async createAccreditation({dispatch, getters}) {
      let error;

      try {
        const response = await api.post('account/accreditation/create');
        const {status, result, errorCode} = response.data;

        if (status === 200) {
          await dispatch('handleProfile', {
            profile: result,
            currentUser: result.id === getters.currentUser.id
          })
        }
        else {
          throw new api.Errors(errorCode);
        }
      }
      catch (e) {
        console.error('Something went wrong while creating accreditation', e);
        error = e;
      }

      if (error) {
        throw error;
      }
    },

    async setAccreditationDocumentExpirationDate({commit}, {documentId, date}) {
      let error;

      try {
        const response = await api.put(`account/${documentId}/attach/${+date}`);
        const {status, errorCode, result} = response.data;

        if (status === 200) {
          commit('setAccreditation', result);
        }
        else {
          error = new api.Errors(errorCode);
        }
      }
      catch (e) {
        throw e;
      }

      if (error) {
        throw error;
      }
    },

    async attachFile({commit}, {documentId, sign, file}) {
      let error;

      try {
        const data = new FormData();
        data.append('sign', sign);
        data.append('file', file);

        const response = await api.post(`account/${documentId}/attach`, data);
        const {status, errorCode, result, content} = response.data;

        if (status === 200) {
          commit('setAccreditation', result);
        }
        else {
          error = new api.Errors(errorCode, { details: (content || [])[0] });
        }
      }
      catch (e) {
        error = e;
      }

      if (error) {
        throw error;
      }
    },

    async removeFile({commit}, {documentId, fileId}) {
      try {
        const response = await api.delete(`account/${documentId}/${fileId}/attach`);
        const {status, errorCode, result} = response.data;

        if (status === 200) {
          console.info(result);
          commit('setAccreditation', result);
        }
        else {
          throw new api.Error(errorCode);
        }
      }
      catch (e) {
        throw e;
      }
    },

    async attachSignature({dispatch}, { text, signature }) {
      try {
        const response = await api.post(`account/sign/attach`, {
          dataForSign: text,
          sign: signature
        });

        const {status, result} = response.data;

        if (status === 200) {
          dispatch('handleProfile', {
            profile: result,
            currentUser: true
          });
        }
      }
      catch(e) {

      }
    },

    async removeSignature({dispatch}, { signId }) {
      try {
        const response = await api.delete(`account/sign/${signId}`);
        const {status, result} = response.data;

        if (status === 200) {
          dispatch('handleProfile', {
            profile: result,
            currentUser: true
          });
        }
      }
      catch(e) {

      }
    },

    async getSigningText() {
      try {
        const response = await api.get(`account/sign`);
        const {status, result} = response.data;

        if (status === 200) {
          console.info(result);
          return result;
        }
        throw status;
      }
      catch (e) {
        throw e;
      }
    },

    async signAccreditation({commit}, {text, signature}) {
      try {
        const response = await api.post(`account/sign`, {
          dataForSign: text,
          sign: signature
        });
        const {status, result, errorCode} = response.data;

        if (status === 200) {
          console.info(result);
          commit('setAccreditation', result);
        }
	else {
          throw new api.Errors(errorCode);
        }
      }
      catch (e) {
	throw e;
      }
    },

    async rejectAccreditation({commit}) {
      try {
        const response = await api.delete(`account`);
        const {status, result} = response.data;

        if (status === 200) {
          console.info(result);
          commit('setAccreditation', result);
        }
      }
      catch (e) {
      }
    },

    async updateAccreditationState({dispatch}, { userId, state, reason, documents}) {
      try {
        const response = await api.put(`account/${userId}/resolution`, {
          state,
          reason: reason || undefined,
          documents: documents.map(doc => ({
            document: doc.id,
            state: doc.state,
            reason: doc.reason || undefined,
            days: doc.days || undefined
          }))
        });
        const {status, result, errorCode} = response.data;

        if (status === 200) {
          await dispatch('handleProfile', { profile: result });
        } else {
          throw new api.Errors(errorCode);
        }
      }
      catch (e) {
      }
    },

    async updateSignatureState({dispatch}, { userId, signId, state, reason }) {
      try {
        const response = await api.put(`/account/${userId}/sign/${signId}`, {
          state,
          reason: reason || undefined
        });
        const {status, result, errorCode} = response.data;

        if (status === 200) {
          await dispatch('handleProfile', { profile: result });
        }
        else {
          throw new api.Errors(errorCode);
        }
      }
      catch (e) {
      }
    },

    async changeMainUser({dispatch}, {userId, ownerId}) {
      try {
        const response = await api.put(`/organization/${userId}/mainUsers/${ownerId}`);
        const {status, result, errorCode} = response.data;

        if (status === 200) {
          await dispatch('handleProfile', { profile: result });
        }
        else {
          throw new api.Errors(errorCode);
        }
      }
      catch (e) {
      }
    },

    async changeCustomerUserInfo({dispatch}, { userId, role, mainUserId, inCommission}) {
      let error;

      try {
        const response = await api.post(`/organization/setDataUser/${userId}/${role}/${mainUserId}/${inCommission}`);
        const {status, result, errorCode} = response.data;

        if (status === 200) {
          await dispatch('handleProfile', { profile: result });
        }
        else {
          throw new api.Errors(errorCode);
        }
      }
      catch (e) {
        error = e;
      }

      if (error) {
        throw error;
      }
    },

    async updateOrgByAdmin({dispatch}, {userId, shortName}) {
      try {
        const response = await api.put(`/organization/${userId}/admin`, { shortName });
        const {status, result, errorCode} = response.data;

        if (status === 200) {
          await dispatch('handleProfile', { profile: result });
        }
        else {
          throw new api.Errors(errorCode);
        }
      }
      catch (e) {
        throw e;
      }
    },

    async blockUser({dispatch, getters}, { id }) {
      let error;

      try {
        const url = (id === getters.currentUser.id ? 'account/block' : `account/${id}/block/true`);

        const response = await api.put(url);
        const {status, result, errorCode} = response.data;

        if (status === 200) {
          await dispatch('handleProfile', {
            profile: result,
            currentUser: result.id === getters.currentUser.id
          });
        }
        else {
          error = new api.Errors(errorCode);
        }
      }
      catch (e) {
        error = e;
      }

      if (error) {
        throw error;
      }
    },

    async unblockUser({dispatch, getters}, { id }) {
      let error;

      try {
        const response = await api.put(`account/${id}/block/false`);
        const {status, result, errorCode} = response.data;

        if (status === 200) {
          await dispatch('handleProfile', {
            profile: result,
            currentUser: result.id === getters.currentUser.id
          });
        }
        else {
          error = new api.Errors(errorCode);
        }
      }
      catch (e) {
        error = e
      }

      if (error) {
        throw error;
      }
    },

    async isLastUser({}, { userId }) {
      let error;

      try {
        const response = await api.get(`account/${userId}/checkLast`);
        const {status, result, errorCode} = response.data;

        if (status === 200) {
          return !!result;
        }
        else {
          error = new api.Errors(errorCode);
        }
      }
      catch (e) {
        error = e;
      }

      if (error) {
        throw error;
      }
    },

    async archiveUser({dispatch, getters}, { id }) {
      let error;

      try {
        const response = await api.delete(`account/${id}/archive`);
        const {status, result, errorCode} = response.data;

        if (status === 200) {
          await dispatch('handleProfile', {
            profile: result,
            currentUser: result.id === getters.currentUser.id
          });
        }
        else {
          error = new api.Errors(errorCode);
        }
      }
      catch (e) {
        error = e;
      }

      if (error) {
        throw error;
      }
    },

    async checkAccountFileSignature({}, {userId, accreditationId, docId, fileId}) {
      try {
        const response = await api.get(`account/info/sign/${userId}/${accreditationId}/${docId}/${fileId}`);
        const {status, result, errorCode} = response.data;

        if (status === 200) {
          return {
            data: result.data,
            signature: result.sign,
            certificates: result.certs.map(e => ({
              ...e,
              time: new Date(e.time)
            }))
          }
        }
        else {
          throw new api.Errors(errorCode);
        }
      }
      catch (e) {
      }
    },

    async checkAccountAccreditationSignature({}, { userId, accreditationId }) {
      try {
        const response = await api.get(`account/info/sign/${userId}/${accreditationId}/0/0`);
        const {status, result, errorCode} = response.data;

        if (status === 200) {
          return {
            data: result.data,
            signature: result.sign,
            certificates: result.certs.map(e => ({
              ...e,
              time: new Date(e.time)
            }))
          }
        }
        else {
          throw new api.Errors(errorCode);
        }
      }
      catch (e) {
      }
    },

    async checkAccountAccreditation({}, { userId }) {
      try {
        const response = await api.get(`account/verifyAccreditation/${userId}`);
        const {status, result, errorCode} = response.data;

        // fio:[] - массив пользователей
        // fullName
        // shortName
        // uid
        // epUser
        // epOrganization

        if (status === 200) {
          return {
            sameLastName: result.fio,
            sameFullName: result.fullName,
            sameShortName: result.shortName,
            sameINN: result.uid,
            sameUserInSignature: result.epUser,
            sameOrganizationInSignature: result.epOrganization
          }
        }
        else {
          throw new api.Errors(errorCode);
        }
      }
      catch (e) {
      }
    },

    async checkSignature({}, { userId, signId }) {
      try {
        const response = await api.get(`/account/info/sign/${userId}/${signId}`);
        const {status, result, errorCode} = response.data;

        if (status === 200) {
          return {
            data: result.data,
            signature: result.sign,
            certificates: result.certs.map(e => ({
              ...e,
              time: new Date(e.time)
            }))
          }
        }
        else {
          throw new api.Errors(errorCode);
        }
      }
      catch (e) {
      }
    },

    async getNotifications({commit}) {
      let error;

      try {
        const response = await api.get(`/account/notifications`);
        const {status, result, errorCode} = response.data;

        if (status === 200) {
          commit('setNotifications', result);
        }
        else {
          throw new api.Errors(errorCode);
        }
      }
      catch(e) {
        error = e;
      }

      if (error) {
        throw error;
      }
    },

    async markReadNotifications({dispatch, getters}, { objectId, type }) {
      let error;

      try {
        const response = await api.post(`/account/readNotification`, {
          objectId,
          type: type.valueOf()
        });
        const {status, result, errorCode} = response.data;

        if (status === 200) {
          return await dispatch('handleProfile', {
            profile: result,
            currentUser: result.id === getters.currentUser.id
          });
        }
        else {
          error = new api.Errors(errorCode);
        }
      }
      catch(e) {
        error = e;
      }

      if (error) {
        throw error;
      }
    },

    async markReadAllNotifications({dispatch}) {
      return await dispatch('markReadNotifications', {
        objectId: 0,
        type: 'None'
      });
    }
  }
}
